import { useEffect, useRef, useState } from "react";
import { Loader2, Image as ImageIcon, CheckCircle2, AlertTriangle, Upload, X, Search, Copy, Check, Clock, Wrench, XCircle, MessageSquare } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";

const TITLE_MAX = 200;
const DESC_MAX = 5000;
const MAX_IMAGE_MB = 8;
const MAX_IMAGES = 6;

// Status config (colors match the admin panel).
const STATUSES = {
    pending: { label: "Oczekujące", icon: Clock, badge: "bg-sky-400/15 text-sky-300 border-sky-400/30" },
    in_progress: { label: "W trakcie", icon: Wrench, badge: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
    done: { label: "Wykonane", icon: CheckCircle2, badge: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
    cancelled: { label: "Anulowane", icon: XCircle, badge: "bg-red-400/15 text-red-300 border-red-400/30" },
};
const statusCfg = (s) => STATUSES[s] || STATUSES.pending;

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("pl-PL", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

let _imgUid = 0;

export default function ReportBug() {
    const { t, lang } = useLang();
    const [modInfo, setModInfo] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [images, setImages] = useState([]); // [{ id, file, preview }]
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submittedTicket, setSubmittedTicket] = useState("");
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Status lookup section state
    const [lookupTicket, setLookupTicket] = useState("");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState("");
    const [lookupResult, setLookupResult] = useState(null);

    const copyTicket = async () => {
        try {
            await navigator.clipboard.writeText(submittedTicket);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard not available */
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setModInfo(data);
            } catch {
                /* keep defaults */
            }
        })();
        trackPageview("/zglos-blad");
    }, []);

    const onLookup = async (e) => {
        e.preventDefault();
        const code = lookupTicket.trim();
        if (!code) {
            setLookupError("Wpisz numer zgłoszenia.");
            return;
        }
        setLookupLoading(true);
        setLookupError("");
        setLookupResult(null);
        try {
            const { data } = await api.get(`/bug-reports/lookup/${encodeURIComponent(code)}`);
            setLookupResult(data);
        } catch (err) {
            if (err.response?.status === 404) {
                setLookupError("Nie znaleziono zgłoszenia o tym numerze. Sprawdź, czy numer jest poprawny.");
            } else {
                setLookupError(
                    formatApiErrorDetail(err.response?.data?.detail) ||
                        "Nie udało się sprawdzić statusu. Spróbuj ponownie."
                );
            }
        } finally {
            setLookupLoading(false);
        }
    };

    const handleFiles = (fileList) => {
        setError("");
        const incoming = Array.from(fileList || []);
        if (incoming.length === 0) return;

        setImages((prev) => {
            const next = [...prev];
            for (const f of incoming) {
                if (next.length >= MAX_IMAGES) {
                    setError(`Możesz dodać maksymalnie ${MAX_IMAGES} zdjęć.`);
                    break;
                }
                if (!f.type.startsWith("image/")) {
                    setError("Każdy załączony plik musi być obrazem.");
                    continue;
                }
                if (f.size > MAX_IMAGE_MB * 1024 * 1024) {
                    setError(`Obraz „${f.name}" jest zbyt duży (max ${MAX_IMAGE_MB} MB).`);
                    continue;
                }
                const id = ++_imgUid;
                const entry = { id, file: f, preview: "" };
                next.push(entry);
                const reader = new FileReader();
                reader.onload = (e) => {
                    const dataUrl = e.target?.result || "";
                    setImages((cur) =>
                        cur.map((it) => (it.id === id ? { ...it, preview: dataUrl } : it))
                    );
                };
                reader.readAsDataURL(f);
            }
            return next;
        });
        // allow selecting the same file again later
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeImage = (id) => {
        setImages((prev) => prev.filter((it) => it.id !== id));
        setError("");
    };

    const onDragOver = (e) => {
        e.preventDefault();
        if (!dragOver) setDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer?.files;
        if (dropped && dropped.length) handleFiles(dropped);
    };

    const clearImages = () => {
        setImages([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const resetForm = () => {
        setTitle("");
        setDescription("");
        clearImages();
        setError("");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const t = title.trim();
        const d = description.trim();
        if (t.length < 3) {
            setError("Tytuł musi mieć przynajmniej 3 znaki.");
            return;
        }
        if (d.length < 5) {
            setError("Opis musi mieć przynajmniej 5 znaków.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("title", t);
            fd.append("description", d);
            images.forEach((it) => fd.append("files", it.file));
            const { data } = await api.post("/bug-reports", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSubmittedTicket(data?.ticket || "");
            setSuccess(true);
            resetForm();
        } catch (err) {
            setError(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    "Nie udało się wysłać zgłoszenia. Spróbuj ponownie."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="report-bug-page"
            className="min-h-screen flex flex-col bg-[color:var(--bg-base)]"
        >
            <SEO
                modInfo={modInfo}
                pathname="/zglos-blad"
                title="Zgłoś błąd"
                description="Zgłoś błąd w modyfikacji Project Ruda Śląska - formularz zgłoszeń wraz z możliwością sprawdzenia statusu po numerze ticketu."
            />
            <Header />
            <main className="flex-1 pt-32 pb-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="label-eyebrow mb-4">{t("Zgłoszenia")}</div>
                    <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tighter text-white mb-6">
                        {lang === "en" ? t("Zgłoś błąd") : <>Zgłoś<br />błąd</>}
                    </h1>
                    <p className="text-base text-[color:var(--text-secondary)] leading-relaxed max-w-xl mb-10">
                        {t("Znalazłeś jakiś błąd? Wypełnij krótki formularz, możesz dodać screeny, to zawsze bardzo pomaga. Upewnij się, że błędu nie powoduje inna modyfikacja.")}
                    </p>

                    {success ? (
                        <div
                            data-testid="report-bug-success"
                            className="panel-yellow p-8 flex flex-col items-start gap-4"
                        >
                            <div className="flex items-center gap-3 text-[color:var(--primary)]">
                                <CheckCircle2 size={28} strokeWidth={2} />
                                <div className="font-display uppercase tracking-wider text-xl text-white">
                                    {t("Dziękujemy!")}
                                </div>
                            </div>
                            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                                {t("Zgłoszenie zostało wysłane. Zespół przejrzy je tak szybko, jak to możliwe.")}
                            </p>

                            {submittedTicket && (
                                <div className="w-full rounded-lg border border-[color:var(--primary)]/40 bg-[color:var(--primary)]/[0.06] p-5">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2">
                                        {t("Twój numer zgłoszenia")}
                                    </div>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span
                                            data-testid="report-bug-ticket"
                                            className="font-display text-2xl md:text-3xl tracking-wider text-[color:var(--primary)] select-all"
                                        >
                                            {submittedTicket}
                                        </span>
                                        <button
                                            type="button"
                                            data-testid="report-bug-ticket-copy"
                                            onClick={copyTicket}
                                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border border-white/15 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30"
                                        >
                                            {copied ? <Check size={13} /> : <Copy size={13} />}
                                            {copied ? t("Skopiowano") : t("Kopiuj")}
                                        </button>
                                    </div>
                                    <p className="flex items-start gap-2 text-xs text-[color:var(--text-secondary)] leading-relaxed mt-3">
                                        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[color:var(--primary)]" />
                                        <span>
                                            <strong className="text-white">{t("Zapisz ten numer.")}</strong>{" "}
                                            {t("Dzięki niemu sprawdzisz status swojego zgłoszenia poniżej w sekcji „Zobacz status zgłoszenia”.")}
                                        </span>
                                    </p>
                                </div>
                            )}

                            <button
                                type="button"
                                data-testid="report-bug-new"
                                onClick={() => {
                                    setSuccess(false);
                                    setSubmittedTicket("");
                                }}
                                className="btn-primary mt-2"
                            >
                                {t("Wyślij kolejne zgłoszenie")}
                            </button>
                        </div>
                    ) : (
                        <form
                            data-testid="report-bug-form"
                            onSubmit={onSubmit}
                            className="panel-yellow p-6 md:p-8 flex flex-col gap-5"
                        >
                            <div>
                                <label
                                    htmlFor="bug-title"
                                    className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2"
                                >
                                    {t("Tytuł")} <span className="text-[color:var(--primary)]">*</span>
                                </label>
                                <input
                                    id="bug-title"
                                    data-testid="bug-title-input"
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                                    placeholder={t("np. lewitujący znak")}
                                    maxLength={TITLE_MAX}
                                    className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--primary)]"
                                    required
                                />
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-1 text-right">
                                    {title.length}/{TITLE_MAX}
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="bug-description"
                                    className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2"
                                >
                                    {t("Opis")} <span className="text-[color:var(--primary)]">*</span>
                                </label>
                                <textarea
                                    id="bug-description"
                                    data-testid="bug-description-input"
                                    value={description}
                                    onChange={(e) =>
                                        setDescription(e.target.value.slice(0, DESC_MAX))
                                    }
                                    rows={6}
                                    placeholder={t("Co się stało, gdzie znajduję się błąd, wersja moda, ewentualne inne wgrane modyfikacje.")}
                                    maxLength={DESC_MAX}
                                    className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--primary)] resize-y"
                                    required
                                />
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-1 text-right">
                                    {description.length}/{DESC_MAX}
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2">
                                    {t(`Zdjęcia / zrzuty ekranu (opcjonalnie) - do ${MAX_IMAGES}`)}
                                </label>

                                {images.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                        {images.map((it) => (
                                            <div
                                                key={it.id}
                                                data-testid={`bug-image-thumb-${it.id}`}
                                                className="relative group rounded-md overflow-hidden border border-white/10 bg-[color:var(--bg-base)] aspect-square"
                                            >
                                                {it.preview ? (
                                                    <img
                                                        src={it.preview}
                                                        alt={it.file?.name || "Podgląd"}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[color:var(--text-muted)]">
                                                        <Loader2 size={18} className="animate-spin" />
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid={`bug-image-remove-${it.id}`}
                                                    onClick={() => removeImage(it.id)}
                                                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[color:var(--bg-base)]/85 backdrop-blur-sm border border-white/10 text-white hover:text-[color:var(--primary)] hover:border-[color:var(--primary)]/40 flex items-center justify-center"
                                                    aria-label="Usuń zdjęcie"
                                                >
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {images.length < MAX_IMAGES && (
                                    <label
                                        htmlFor="bug-image"
                                        data-testid="bug-image-dropzone"
                                        onDragOver={onDragOver}
                                        onDragEnter={onDragOver}
                                        onDragLeave={onDragLeave}
                                        onDrop={onDrop}
                                        className={`flex flex-col items-center justify-center gap-2 border border-dashed rounded-md p-6 cursor-pointer transition-colors ${
                                            dragOver
                                                ? "border-[color:var(--primary)] bg-[color:var(--primary)]/[0.08]"
                                                : "border-white/15 hover:border-[color:var(--primary)]/50 hover:bg-white/[0.02]"
                                        }`}
                                    >
                                        <ImageIcon
                                            size={28}
                                            className={dragOver ? "text-[color:var(--primary)]" : "text-[color:var(--text-muted)]"}
                                            strokeWidth={1.5}
                                        />
                                        <span className="text-sm text-[color:var(--text-secondary)] text-center">
                                            {dragOver
                                                ? t("Upuść zdjęcia tutaj")
                                                : lang === "en"
                                                  ? t("Przeciągnij i upuść zdjęcia lub kliknij, aby dodać")
                                                  : <>Przeciągnij i upuść zdjęcia lub <span className="text-[color:var(--primary)]">kliknij</span>, aby dodać {images.length > 0 ? "kolejne" : ""}</>}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                            PNG / JPG / WEBP · max {MAX_IMAGE_MB} MB {t("każdy")} · {images.length}/{MAX_IMAGES}
                                        </span>
                                    </label>
                                )}
                                <input
                                    id="bug-image"
                                    ref={fileInputRef}
                                    data-testid="bug-image-input"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => handleFiles(e.target.files)}
                                    className="hidden"
                                />
                            </div>

                            {error && (
                                <div
                                    data-testid="report-bug-error"
                                    className="flex items-start gap-2 text-sm text-red-400 bg-red-500/5 border border-red-500/30 rounded-md px-3 py-2"
                                >
                                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                    <span>{t(error)}</span>
                                </div>
                            )}

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="submit"
                                    data-testid="report-bug-submit"
                                    disabled={submitting}
                                    className="btn-primary"
                                >
                                    {submitting ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <Upload size={14} />
                                    )}
                                    {submitting ? t("Wysyłanie…") : t("Wyślij zgłoszenie")}
                                </button>
                                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                    {t("Pola oznaczone")} <span className="text-[color:var(--primary)]">*</span> {t("są wymagane")}
                                </span>
                            </div>
                        </form>
                    )}

                    {/* ---- Status lookup section ---- */}
                    <section
                        data-testid="status-lookup-section"
                        className="mt-16 pt-12 border-t border-white/10"
                    >
                        <div className="label-eyebrow mb-4">{t("Sprawdź status")}</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tighter text-white mb-4">
                            {lang === "en" ? t("Zobacz status zgłoszenia") : <>Zobacz status<br />zgłoszenia</>}
                        </h2>
                        <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed max-w-xl mb-6">
                            {t("Masz już numer zgłoszenia (np. PR-A1B2C3)? Wpisz go poniżej, aby zobaczyć aktualny status nadany przez zespół.")}
                        </p>

                        <form
                            onSubmit={onLookup}
                            data-testid="status-lookup-form"
                            className="flex flex-col sm:flex-row gap-3 max-w-xl"
                        >
                            <input
                                data-testid="status-lookup-input"
                                type="text"
                                value={lookupTicket}
                                onChange={(e) => setLookupTicket(e.target.value.toUpperCase())}
                                placeholder={t("np. PR-A1B2C3")}
                                className="flex-1 bg-[color:var(--bg-surface)] border border-white/10 rounded-md px-4 py-3 text-white text-sm tracking-wider focus:outline-none focus:border-[color:var(--primary)]"
                            />
                            <button
                                type="submit"
                                data-testid="status-lookup-submit"
                                disabled={lookupLoading}
                                className="btn-primary justify-center"
                            >
                                {lookupLoading ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Search size={14} />
                                )}
                                {t("Sprawdź status")}
                            </button>
                        </form>

                        {lookupError && (
                            <div
                                data-testid="status-lookup-error"
                                className="flex items-start gap-2 text-sm text-red-400 bg-red-500/5 border border-red-500/30 rounded-md px-3 py-2 mt-4 max-w-xl"
                            >
                                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                <span>{t(lookupError)}</span>
                            </div>
                        )}

                        {lookupResult && (() => {
                            const cfg = statusCfg(lookupResult.status);
                            const SIcon = cfg.icon;
                            const imgs = Array.isArray(lookupResult.image_urls)
                                ? lookupResult.image_urls
                                : [];
                            return (
                                <div
                                    data-testid="status-lookup-result"
                                    className="panel-yellow p-6 mt-6 max-w-xl"
                                >
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <span className="font-display tracking-wider text-[color:var(--primary)]">
                                            {lookupResult.ticket}
                                        </span>
                                        <span
                                            data-testid="status-lookup-badge"
                                            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${cfg.badge}`}
                                        >
                                            <SIcon size={12} /> {t(cfg.label)}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                            {formatDate(lookupResult.created_at)}
                                        </span>
                                    </div>
                                    <h3 className="text-white font-medium break-words">
                                        {t(lookupResult.title)}
                                    </h3>
                                    <p className="text-sm text-[color:var(--text-secondary)] mt-1.5 whitespace-pre-wrap break-words">
                                        {t(lookupResult.description)}
                                    </p>
                                    {imgs.length > 0 && (
                                        <div className="grid grid-cols-3 gap-2 mt-4">
                                            {imgs.map((u, idx) => (
                                                <a
                                                    key={idx}
                                                    href={resolveMediaUrl(u)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block rounded-md overflow-hidden border border-white/10 bg-[color:var(--bg-base)] aspect-square"
                                                >
                                                    <img
                                                        src={resolveMediaUrl(u)}
                                                        alt={`Zrzut ${idx + 1}`}
                                                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                    {lookupResult.admin_comment && (
                                        <div
                                            data-testid="status-lookup-comment"
                                            className="mt-4 rounded-md border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/[0.06] p-4"
                                        >
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[color:var(--primary)] mb-1.5">
                                                <MessageSquare size={12} /> {t("Komentarz zespołu")}
                                            </div>
                                            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap break-words">
                                                {t(lookupResult.admin_comment)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
}
